import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectStripe } from 'react-stripe-elements';
import qs from 'query-string';
import { injectIntl, defineMessages } from 'react-intl';

import api from 'api';
import { fetchFlagsRequest } from 'modules/flags/redux/actionCreators';
import { selectStripeCheckoutEnabled } from 'modules/flags/redux/selectors';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import Checkout from '../components/Checkout';
import * as actionCreators from '../redux/actionCreators';
import * as selectors from '../redux/selectors';
import { getSelectedIds } from '../helpers';

const currency = 'SEK';
const IS_BILLOGRAM_ENABLED = false;

const messages = defineMessages({
  pollTimeout: {
    id: 'payment.pollTimeout.message',
    defaultMessage:
      'We’re still confirming your payment. Invoice/bank methods can take a couple of minutes. Press "Try Again" to update the status.',
  },
  tryAgain: {
    id: 'ui.tryAgain',
    defaultMessage: 'Try Again',
  },
});

class BuyAddonsContainer extends React.Component {
  state = {
    error: null,
    isRedirecting: false,
    isVerifyingPayment: false,
  };

  _isMounted = false;

  _pollTimer = null;

  _currentPollingKey = null; // `${cottageId}:${orderId}`

  _onVisibilityChange = null;

  componentDidMount() {
    this._isMounted = true;

    const { match } = this.props;
    const { cottageId } = match.params;

    // Centralized handling of return params (cancel first)
    this.handleCheckoutReturn();

    // Always fetch products and flags
    this.props.fetchPaymentProductsRequest({ cottageId });
    this.props.fetchFlagsRequest(); // HTTP cache respects server TTL

    // Visibility pause/resume
    this._onVisibilityChange = () => {
      if (!this._isMounted) return;
      if (document.hidden) {
        if (this._pollTimer) {
          clearTimeout(this._pollTimer);
          this._pollTimer = null;
        }
      } else {
        const q = this.getQuery();
        // If we were polling and still not done, resume one tick - but never on cancel
        if (
          q.source === 'stripe_checkout' &&
          q.checkout !== 'cancel' &&
          q.orderId &&
          this.props.activeStep !== 2
        ) {
          this.beginCheckoutPolling({ cottageId, orderId: q.orderId });
        }
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange, false);
  }

  componentDidUpdate(prevProps) {
    // If the query string changed, re-handle return state (covers router replaces)
    if (prevProps.history.location.search !== this.props.history.location.search) {
      this.handleCheckoutReturn();
    }

    // When we transition to Done, clean URL immediately (don’t wait for poll tick)
    if (this._isMounted && prevProps.activeStep !== 2 && this.props.activeStep === 2) {
      if (this._pollTimer) {
        clearTimeout(this._pollTimer);
        this._pollTimer = null;
      }
      this._currentPollingKey = null;
      this.safeCleanupStripeParams();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }

    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange, false);
      this._onVisibilityChange = null;
    }

    // Keep success sticky, otherwise reset if unmounting before Done (activeStep 2)
    if (this.props.activeStep !== 2) {
      this.props.paymentResetFlow();
    }
  }

  onCloseModal = () => {
    this.props.paymentSetActiveStep({ activeStep: 0 });
  };

  getCottageId = () => {
    const { cottageId } = this.props.match.params;

    return cottageId;
  };

  setPaymentMethod = (paymentMethod) => {
    this.props.paymentSetMethod({ paymentMethod });
  };

  getQuery = () => {
    const { search } = this.props.history.location;
    return qs.parse(search, { parseBooleans: true });
  };

  getReturnParams = () => {
    const q = this.getQuery();
    return {
      orderId: q.orderId,
      source: q.source,
      checkout: q.checkout,
      sessionId: q.session_id,
    };
  };

  handleCheckoutReturn = () => {
    const { fetchPaymentOrderRequest, match } = this.props;
    const { cottageId } = match.params;

    const q = this.getQuery();
    const { orderId, source, checkout, session_id: sessionId } = q;

    const hasStripeParams = source === 'stripe_checkout' || !!checkout || !!sessionId;

    // If no Stripe-related params and no orderId, this is a fresh visit -> reset flow
    if (!orderId && !hasStripeParams) {
      this.props.paymentResetFlow();
      return;
    }

    // Cancel: clean first, stop polling, reset via centralized action, then bail
    if (source === 'stripe_checkout' && checkout === 'cancel') {
      this.safeCleanupStripeParams(false); // drop orderId & stripe params
      if (this._pollTimer) {
        clearTimeout(this._pollTimer);
        this._pollTimer = null;
      }
      this._currentPollingKey = null;

      this.props.paymentCancelCheckout();

      if (this._isMounted) {
        this.setState({ isRedirecting: false, isVerifyingPayment: false, error: null });
      }
      return;
    }

    // If we are already done, don’t refetch/poll
    if (this.props.activeStep === 2) return;

    // Normal return / still processing: fetch + poll
    if (orderId) {
      fetchPaymentOrderRequest({ cottageId, orderId, source });
    }

    if (source === 'stripe_checkout' && orderId) {
      if (this._isMounted)
        this.setState({ isRedirecting: false, isVerifyingPayment: true, error: null });
      this.beginCheckoutPolling({ cottageId, orderId });
    }
  };

  // Remove stripe return params but keep orderId if present
  safeCleanupStripeParams = (keepOrderId = true) => {
    const { history } = this.props;
    const { pathname, search } = history.location;
    const q = qs.parse(search, { parseBooleans: true });

    const hasStripeParams =
      q.source === 'stripe_checkout' || Boolean(q.checkout) || Boolean(q.session_id);

    if (!hasStripeParams) return;

    const next = `${pathname}${
      keepOrderId && q.orderId ? `?${qs.stringify({ orderId: q.orderId })}` : ''
    }`;
    const current = `${pathname}${search || ''}`;
    if (next !== current) history.replace(next);
  };

  beginCheckoutPolling = ({ cottageId, orderId, initialDelayMs = 700 }) => {
    const q = this.getQuery();
    if (q.checkout === 'cancel') return; // never poll on cancel
    const { source } = q;
    if (source !== 'stripe_checkout' || !orderId) return;

    // If already Done (activeStep 2), just clean URL
    if (this.props.activeStep === 2) {
      this.safeCleanupStripeParams();
      return;
    }

    const start = Date.now();
    const maxMs = 30_000; // poll <= 30s to bridge webhook lag
    const interval = 1500;
    const key = `${cottageId}:${orderId}`;

    // Skip if we're already polling this order
    if (this._currentPollingKey === key && this._pollTimer) return;
    this._currentPollingKey = key;

    const tick = async () => {
      try {
        const data = await api.fetchOrderStatus(cottageId, orderId);
        if (data && data.paid_at) {
          // success!
          // Mark done and clean URL (drop source/checkout/session_id)
          if (this.props.activeStep !== 2) {
            this.props.paymentSetActiveStep({ activeStep: 2 });
          }
          this.safeCleanupStripeParams();

          if (this._isMounted) this.setState({ isVerifyingPayment: false, error: null });
          if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
          }
          this._currentPollingKey = null;
          return;
        }
        if (['failed', 'expired'].includes(String(data?.payment_status))) {
          if (this._isMounted) {
            this.setState({
              error: { message: `Payment ${data.payment_status}. Please try again.` },
              isVerifyingPayment: false,
            });
          }
          if (this._pollTimer) {
            clearTimeout(this._pollTimer);
            this._pollTimer = null;
          }
          this._currentPollingKey = null;
          return;
        }
      } catch (e) {
        // keep trying until timeout
      }

      if (Date.now() - start >= maxMs) {
        if (this._isMounted) {
          this.setState({
            error: {
              message: this.props.intl.formatMessage(messages.pollTimeout),
              status: 408, // Request Timeout
              timestamp: Date.now(),
              canTryAgain: true,
              tryAgainLabel: this.props.intl.formatMessage(messages.tryAgain),
              code: 'poll_timeout',
            },
            isVerifyingPayment: false,
          });
        }
        if (this._pollTimer) {
          clearTimeout(this._pollTimer);
          this._pollTimer = null;
        }
        this._currentPollingKey = null;
        return;
      }

      this._pollTimer = setTimeout(tick, interval);
    };

    // Disable the Pay button while we’re waiting
    const schedule = () => {
      this._pollTimer = setTimeout(tick, initialDelayMs);
    };
    if (this._isMounted) {
      this.setState({ isVerifyingPayment: true, error: null }, schedule);
    } else {
      schedule();
    }
  };

  handlePollRetry = () => {
    const { cottageId } = this.props.match.params;
    const { orderId } = this.getReturnParams();
    if (!orderId) return;

    // Clear the banner and restart polling immediately
    this.setState({ isVerifyingPayment: true, error: null }, () => {
      this.beginCheckoutPolling({ cottageId, orderId, initialDelayMs: 0 });
    });
  };

  startStripeCheckout = async ({ cottageId, orderId }) => {
    const { stripe } = this.props;

    if (!stripe || !stripe.redirectToCheckout) {
      throw new Error('Stripe.js not initialized');
    }

    const res = await fetch(`/api/cottages/${cottageId}/orders/${orderId}/checkout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status} ${res.statusText}`);
    }

    const { sessionId } = await res.json();
    if (!sessionId) throw new Error('Missing sessionId from /checkout response');

    const { error } = await stripe.redirectToCheckout({ sessionId });
    if (error) throw error;
  };

  submitForm = (values, amount) => {
    const { activeStep, paymentMethod, postPaymentOrderRequest, postPaymentInvoiceRequest, order } =
      this.props;

    const cottageId = this.getCottageId();

    if (activeStep === 0) {
      postPaymentOrderRequest({
        cottageId,
        productIds: getSelectedIds(values),
        callback: (clientSecret) => {
          this.clientSecret = clientSecret;
          this.submitForm(values, amount);
        },
        skipFetchSecret: this.props.isStripeCheckoutEnabled || !!paymentMethod,
      });
    } else if (activeStep === 1 && !paymentMethod) {
      // If Stripe Checkout is enabled, use redirect flow instead of Elements
      if (this.props.isStripeCheckoutEnabled) {
        this.setState({ isRedirecting: true, error: null });
        this.startStripeCheckout({ cottageId, orderId: order.id }).catch((error) =>
          this.setState({ error: { message: error.message }, isRedirecting: false }),
        );
        return;
      }
      // Legacy Elements flow
      this.submitStripe();
    } else {
      if (!IS_BILLOGRAM_ENABLED) {
        this.setState({
          error: { message: 'Invoicing is not available' },
        });
        return;
      }

      const data = {
        address: values.address,
        city: values.city,
        name: values.name,
        zipCode: values.zipCode,
      };

      postPaymentInvoiceRequest({ cottageId, orderId: order.id, data });
    }
  };

  submitStripe = () => {
    const { stripe, paymentSetIsUpdating } = this.props;

    paymentSetIsUpdating({ isUpdating: true });

    stripe.handleCardPayment(this.clientSecret).then(({ error, paymentIntent }) => {
      if (error) {
        this.setState({ error }, () => paymentSetIsUpdating({ isUpdating: false }));
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        this.props.paymentSetActiveStep({ activeStep: 2 });
        paymentSetIsUpdating({ isUpdating: false });
      } else {
        paymentSetIsUpdating({ isUpdating: false });
      }
    });
  };

  handle3dSecure = (token, { amount }) => {
    const { stripe, order } = this.props;

    const { origin, pathname } = window.location;

    this.setState({ isRedirecting: true });

    stripe
      .createSource({
        type: 'three_d_secure',
        amount,
        currency,
        three_d_secure: {
          card: token.card.id,
        },
        redirect: {
          return_url: `${origin}${pathname}?orderId=${order.id}`,
        },
      })
      .then(({ source, error }) => {
        if (source) {
          window.location.href = source.redirect.url;
        } else if (error) {
          this.setState({ error });
        }
      });
  };

  submitCommission = () => {
    const { cottage, paymentSetActiveStep, submitCommission, submitCommissionAndAskForReview } =
      this.props;

    if (!cottage.asked_for_review_at) {
      submitCommissionAndAskForReview();
    } else {
      submitCommission();
    }
    paymentSetActiveStep({ activeStep: 2 });
  };

  render() {
    const {
      activeStep,
      cottage,
      error,
      isLoading,
      isUpdating,
      options,
      order,
      paymentMethod,
      success,
    } = this.props;

    const { source: rawSource } = this.getQuery();
    const returningFromCheckout = rawSource === 'stripe_checkout';

    // Normalize source so <Checkout /> doesn't enter its internal spinner branch
    const source = returningFromCheckout ? undefined : rawSource;

    const reduxError = error && error.message ? error : null;
    const localError = this.state.error && this.state.error.message ? this.state.error : null;
    const isPollTimeout = localError?.code === 'poll_timeout';

    return (
      <>
        <Checkout
          activeStep={activeStep}
          cottage={cottage}
          isLoading={isLoading}
          isRedirecting={this.state.isRedirecting}
          isVerifyingPayment={this.state.isVerifyingPayment}
          isUpdating={isUpdating} // Elements only
          options={options}
          order={order}
          paymentMethod={paymentMethod}
          source={source}
          isBillogramEnabled={IS_BILLOGRAM_ENABLED}
          isStripeCheckoutEnabled={this.props.isStripeCheckoutEnabled}
          setPaymentMethod={this.setPaymentMethod}
          submitCommission={this.submitCommission}
          submitForm={this.submitForm}
        />
        <Success success={success} />
        {reduxError && <Errors isPaymentError={!paymentMethod} errors={reduxError} />}
        {localError && (
          <Errors
            key={localError.timestamp || 'local'}
            isPaymentError={false}
            dismissBehavior={isPollTimeout ? 'persistent' : 'auto'}
            errors={localError}
            onRetry={isPollTimeout ? this.handlePollRetry : undefined}
          />
        )}
      </>
    );
  }
}

BuyAddonsContainer.propTypes = {
  activeStep: PropTypes.number,
  cottage: PropTypes.shape({
    asked_for_review_at: PropTypes.string,
  }),
  error: PropTypes.shape({
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
    status: PropTypes.number,
    timestamp: PropTypes.number,
    canTryAgain: PropTypes.bool,
    tryAgainLabel: PropTypes.string,
    code: PropTypes.string,
  }),
  history: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    replace: PropTypes.func,
  }).isRequired,
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      cottageId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  order: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  paymentMethod: PropTypes.string,
  stripe: PropTypes.shape({
    handleCardPayment: PropTypes.func,
    handle3dSecure: PropTypes.func,
    createSource: PropTypes.func,
    redirectToCheckout: PropTypes.func,
  }).isRequired,
  isStripeCheckoutEnabled: PropTypes.bool,
  success: PropTypes.shape({}),

  fetchPaymentOrderRequest: PropTypes.func.isRequired,
  fetchPaymentProductsRequest: PropTypes.func.isRequired,
  paymentSetActiveStep: PropTypes.func.isRequired,
  paymentSetIsUpdating: PropTypes.func.isRequired,
  paymentSetMethod: PropTypes.func.isRequired,
  postPaymentInvoiceRequest: PropTypes.func.isRequired,
  postPaymentOrderRequest: PropTypes.func.isRequired,
  submitCommission: PropTypes.func.isRequired,
  submitCommissionAndAskForReview: PropTypes.func.isRequired,
  fetchFlagsRequest: PropTypes.func.isRequired,
  paymentResetFlow: PropTypes.func.isRequired,
  paymentCancelCheckout: PropTypes.func.isRequired,
  intl: PropTypes.shape({ formatMessage: PropTypes.func.isRequired }).isRequired,
};

BuyAddonsContainer.defaultProps = {
  cottage: {},
};

const mapStateToProps = (state) => ({
  activeStep: selectors.activeStep(state),
  error: selectors.error(state),
  isLoading: selectors.isLoading(state),
  isUpdating: selectors.isUpdating(state),
  options: selectors.options(state),
  order: selectors.order(state),
  paymentMethod: selectors.paymentMethod(state),
  success: selectors.success(state),
  isStripeCheckoutEnabled: selectStripeCheckoutEnabled(state),
});

const mapDispatchToProp = {
  fetchPaymentOrderRequest: actionCreators.fetchPaymentOrderRequest,
  fetchPaymentProductsRequest: actionCreators.fetchPaymentProductsRequest,
  paymentSetActiveStep: actionCreators.paymentSetActiveStep,
  paymentSetIsUpdating: actionCreators.paymentSetIsUpdating,
  paymentSetMethod: actionCreators.paymentSetMethod,
  postPaymentInvoiceRequest: actionCreators.postPaymentInvoiceRequest,
  postPaymentOrderRequest: actionCreators.postPaymentOrderRequest,
  postPaymentPayRequest: actionCreators.postPaymentPayRequest,
  fetchFlagsRequest,
  paymentResetFlow: actionCreators.paymentResetFlow,
  paymentCancelCheckout: actionCreators.paymentCancelCheckout,
};

const StipeBuyAddonsContainer = injectStripe(BuyAddonsContainer);
const IntlStripeBuyAddonsContainer = injectIntl(StipeBuyAddonsContainer);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProp)(IntlStripeBuyAddonsContainer),
);
